import React, { createContext, useContext, useState, useCallback } from 'react'
import { OrgRecord } from '@/types/organization'
import { setCurrentOrgSlug } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'

interface OrgContextType {
  selectedOrg: OrgRecord | null
  setSelectedOrg: (org: OrgRecord | null) => void
  /**
   * True when the admin UI is operating against exactly one org, whether that
   * org was picked explicitly (platform admin) or is implicit (dedicated
   * tenant). Gate org-scoped nav and pages on this, not on `selectedOrg`.
   */
  hasOrgContext: boolean
  /** Display name of the current org, from either source. */
  orgName: string | null
  /** Slug of the current org, from either source. */
  orgSlug: string | null
}

const OrgContext = createContext<OrgContextType | undefined>(undefined)

const SELECTED_ORG_STORAGE_KEY = 'admin.selectedOrg'

function loadStoredOrg(): OrgRecord | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(SELECTED_ORG_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as OrgRecord) : null
  } catch {
    return null
  }
}

export function OrgProvider({ children }: { children: React.ReactNode }) {
  // Restore the previously selected org so the selection survives the reload
  // triggered on switch (and matches the persisted X-Org-Slug header).
  const [selectedOrg, setSelectedOrgState] = useState<OrgRecord | null>(loadStoredOrg)
  const { user, isPlatformAdmin } = useAuth()

  // On a DEDICATED tenant the org is implicit: there is exactly one, and the
  // backend already resolved it for this request from the subdomain or custom
  // domain. `/api/users/me` echoes that back as `activeOrg`, which is why we
  // read it from there rather than trusting anything client-side.
  //
  // Without this, org context could only ever be established by the switcher on
  // /organizations, which is platform-admin-only. A tenant has no platform
  // admins by design, so its own admin could not reach Org Settings or Members
  // at all: the backend routes were correctly gated at requireOrgRole and would
  // have answered, but the nav items leading to them never rendered.
  //
  // Deliberately limited to non-platform-admins. On the shared deployment a
  // platform admin's requests also resolve to a default org, and treating that
  // as an implicit selection would silently start showing org-scoped pages for
  // an org they never chose. Their explicit switcher flow is unchanged.
  const activeOrg = user?.backendUser?.activeOrg as
    | { slug: string; name: string }
    | null
    | undefined
  const implicitOrg = !isPlatformAdmin && activeOrg?.slug ? activeOrg : null

  const setSelectedOrg = useCallback((org: OrgRecord | null) => {
    setSelectedOrgState(org)
    setCurrentOrgSlug(org?.slug ?? null)
    if (typeof localStorage !== 'undefined') {
      if (org) localStorage.setItem(SELECTED_ORG_STORAGE_KEY, JSON.stringify(org))
      else localStorage.removeItem(SELECTED_ORG_STORAGE_KEY)
    }
    // Reload so every page refetches its data under the newly selected org. All
    // admin data is org-scoped via the X-Org-Slug header, and pages fetch on
    // mount rather than on org-context change, so a reload reliably re-scopes
    // the whole view.
    window.location.reload()
  }, [])

  const value: OrgContextType = {
    selectedOrg,
    setSelectedOrg,
    // Explicitly selected (platform admin) OR implicitly resolved (tenant).
    // The implicit case deliberately does NOT set an X-Org-Slug header: on a
    // dedicated deployment the backend pins the org from the domain, and
    // sending a header would be both redundant and a way to get it wrong.
    hasOrgContext: selectedOrg !== null || implicitOrg !== null,
    orgName: selectedOrg?.name ?? implicitOrg?.name ?? null,
    orgSlug: selectedOrg?.slug ?? implicitOrg?.slug ?? null,
  }

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>
}

export function useOrgContext() {
  const context = useContext(OrgContext)
  if (context === undefined) {
    throw new Error('useOrgContext must be used within an OrgProvider')
  }
  return context
}
